import { useLayoutEffect, useRef, useState } from 'react'

import { TRANSITION_END_EVENT } from '../constants'
import { PositionHandle, TransitionCallback } from '../types'

type TransitionStatus = 'exited' | 'entering' | 'entered' | 'exiting'

interface TransitionState {
  status: TransitionStatus
}

type NextCallback = ((event?: Event) => void) & { cancel: () => void }

interface PendingCallback {
  state: TransitionState
  callback: NextCallback
}

interface TransitionInstance {
  element: HTMLElement | null
  nextCallback: NextCallback | null
  removeTransitionEndListener: (() => void) | null
  pendingCallbacks: PendingCallback[]
}

export interface UseTransitionStatusParams {
  isIn: boolean
  timeout: number
  onEnter?: TransitionCallback
  onEntered?: TransitionCallback
  onExit?: TransitionCallback
  onExited?: TransitionCallback
}

interface StatusChangeParams {
  status: TransitionStatus
  callback: () => void
}

interface TransitionEndParams {
  node: HTMLElement
  handler: () => void
}

interface PerformParams {
  status: TransitionStatus
  finalStatus: TransitionStatus
  onStart?: TransitionCallback
  onFinish?: TransitionCallback
}

export const useTransitionStatus = ({
  isIn,
  timeout,
  onEnter,
  onEntered,
  onExit,
  onExited,
}: UseTransitionStatusParams) => {
  const [state, setState] = useState<TransitionState>({ status: 'exited' })
  const childRef = useRef<PositionHandle>(null)
  const instanceRef = useRef<TransitionInstance>({
    element: null,
    nextCallback: null,
    removeTransitionEndListener: null,
    pendingCallbacks: [],
  })

  const setNextCallback = (callback: () => void) => {
    const instance = instanceRef.current
    let isActive = true

    const nextCallback: NextCallback = Object.assign(
      (event?: Event) => {
        if (!isActive) {
          return
        }

        if (event && instance.element !== event.target) {
          return
        }

        callback()
        isActive = false
        if (instance.nextCallback === nextCallback) {
          instance.nextCallback = null
        }
      },
      {
        cancel: () => {
          isActive = false
        },
      },
    )

    instance.nextCallback = nextCallback
    return nextCallback
  }

  const cancelNextCallback = () => {
    const instance = instanceRef.current
    instance.nextCallback?.cancel()
    instance.nextCallback = null
  }

  const onTransitionEnd = ({ node, handler }: TransitionEndParams) => {
    const instance = instanceRef.current
    const callback = setNextCallback(handler)

    instance.removeTransitionEndListener?.()

    node.addEventListener(TRANSITION_END_EVENT, callback)
    instance.removeTransitionEndListener = () =>
      node.removeEventListener(TRANSITION_END_EVENT, callback)
    setTimeout(callback, timeout)
  }

  const safeSetState = ({ status, callback }: StatusChangeParams) => {
    const instance = instanceRef.current
    if (instance.element) {
      const nextState = { status }
      instance.pendingCallbacks.push({ state: nextState, callback: setNextCallback(callback) })
      setState(nextState)
    }
  }

  const perform = ({ status, finalStatus, onStart, onFinish }: PerformParams) => {
    cancelNextCallback()
    const node = childRef.current?.child ?? null

    instanceRef.current.element = node
    onStart?.(node)

    if (!node) {
      return
    }

    safeSetState({
      status,
      callback: () =>
        onTransitionEnd({
          node,
          handler: () => safeSetState({ status: finalStatus, callback: () => onFinish?.(node) }),
        }),
    })
  }

  useLayoutEffect(() => {
    const { status } = state

    if (isIn) {
      if (status === 'exiting' || status === 'exited') {
        perform({
          status: 'entering',
          finalStatus: 'entered',
          onStart: onEnter,
          onFinish: onEntered,
        })
      }
    } else if (status === 'entering' || status === 'entered') {
      perform({ status: 'exiting', finalStatus: 'exited', onStart: onExit, onFinish: onExited })
    }
  })

  useLayoutEffect(() => {
    const { pendingCallbacks } = instanceRef.current
    const appliedIndex = pendingCallbacks.findIndex(pending => pending.state === state)

    if (appliedIndex === -1) {
      return
    }

    pendingCallbacks.splice(0, appliedIndex + 1).forEach(pending => pending.callback())
  })

  useLayoutEffect(() => {
    const instance = instanceRef.current

    return () => {
      instance.nextCallback?.cancel()
      instance.nextCallback = null
      instance.element = null
    }
  }, [])

  return { status: state.status, childRef }
}
